import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Users, FileText, CircleCheck, CircleX, Search, ShieldAlert, Trash2 } from "lucide-react";
import Navbar, { GoogleLoginButton } from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_BADGE = {
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  accepted: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  rejected: "bg-red-500/15 text-red-400 border-red-500/40",
};

export default function Admin() {
  const { user, loading, isAdmin } = useAuth();
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [deciding, setDeciding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setFetching(true);
    try {
      const [appsRes, usersRes] = await Promise.all([
        api.get("/admin/applications"),
        api.get("/admin/users"),
      ]);
      setApplications(appsRes.data.applications || []);
      setUsers(usersRes.data.users || []);
    } catch {
      toast.error("Could not load admin data");
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const decide = async (action) => {
    if (!selected) return;
    setDeciding(true);
    try {
      const res = await api.post(`/admin/applications/${selected.application_id}/decision`, { action, note });
      toast.success(action === "accept" ? "Applicant whitelisted" : "Application rejected");
      const updated = res.data;
      setApplications((apps) => apps.map((a) => (a.application_id === updated.application_id ? updated : a)));
      setUsers((us) =>
        us.map((u) => (u.user_id === updated.user_id ? { ...u, application_status: updated.status } : u))
      );
      setSelected(null);
      setNote("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setDeciding(false);
    }
  };

  const deleteUser = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.user_id}`);
      toast.success(`${deleteTarget.name} removed from Southside`);
      setUsers((us) => us.filter((u) => u.user_id !== deleteTarget.user_id));
      setApplications((apps) => apps.filter((a) => a.user_id !== deleteTarget.user_id));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-plum-900 flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-sunset-amber" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-plum-900" data-testid="admin-login-prompt">
        <Navbar />
        <div className="max-w-md mx-auto px-4 pt-40 pb-24 text-center">
          <h1 className="font-display text-3xl font-extrabold uppercase text-cream mb-4">Staff Only</h1>
          <p className="text-muted-foreground text-sm mb-8">Sign in with your staff account to continue.</p>
          <GoogleLoginButton />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-plum-900" data-testid="admin-access-denied">
        <Navbar />
        <div className="max-w-md mx-auto px-4 pt-40 pb-24 text-center">
          <ShieldAlert className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h1 className="font-display text-3xl font-extrabold uppercase text-cream mb-4">Access Denied</h1>
          <p className="text-muted-foreground text-sm">This area is restricted to Southside staff.</p>
        </div>
      </div>
    );
  }

  const filteredApps = applications.filter((a) => (filter === "all" ? true : a.status === filter));
  const filteredUsers = users.filter(
    (u) =>
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase())
  );
  const counts = {
    pending: applications.filter((a) => a.status === "pending").length,
    accepted: applications.filter((a) => a.status === "accepted").length,
    rejected: applications.filter((a) => a.status === "rejected").length,
  };

  return (
    <div className="min-h-screen bg-plum-900" data-testid="admin-dashboard">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-cream">
          Staff <span className="sunset-text">Control Panel</span>
        </h1>
        <p className="text-muted-foreground text-sm mt-2 mb-8">Manage whitelist applications and citizens.</p>

        <Tabs defaultValue="overview">
          <TabsList className="bg-plum-700 border border-sunset-amber/20 mb-8">
            <TabsTrigger value="overview" data-testid="admin-tab-overview" className="data-[state=active]:bg-sunset-amber data-[state=active]:text-plum-900">Overview</TabsTrigger>
            <TabsTrigger value="applications" data-testid="admin-tab-applications" className="data-[state=active]:bg-sunset-amber data-[state=active]:text-plum-900">
              Applications {counts.pending > 0 && <span className="ml-2 rounded-full bg-sunset-orange text-cream text-[10px] px-2 py-0.5">{counts.pending}</span>}
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users" className="data-[state=active]:bg-sunset-amber data-[state=active]:text-plum-900">Users</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {[
                { icon: Users, label: "Total Users", value: users.length, testid: "admin-kpi-users" },
                { icon: FileText, label: "Pending Apps", value: counts.pending, testid: "admin-kpi-pending" },
                { icon: CircleCheck, label: "Whitelisted", value: counts.accepted, testid: "admin-kpi-accepted" },
                { icon: CircleX, label: "Rejected", value: counts.rejected, testid: "admin-kpi-rejected" },
              ].map((k) => (
                <div key={k.testid} data-testid={k.testid} className="rounded-2xl border border-sunset-amber/20 bg-plum-700/70 p-5 sm:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <k.icon className="h-5 w-5 text-sunset-amber" />
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{k.label}</span>
                  </div>
                  <p className="font-mono text-2xl sm:text-3xl font-extrabold text-sunset-gold">{k.value}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="applications">
            <div className="flex flex-wrap gap-2 mb-6">
              {["pending", "accepted", "rejected", "all"].map((f) => (
                <button
                  key={f}
                  data-testid={`admin-filter-${f}`}
                  onClick={() => setFilter(f)}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider border transition-colors ${
                    filter === f
                      ? "bg-sunset-amber text-plum-900 border-sunset-amber"
                      : "border-sunset-amber/30 text-cream/70 hover:border-sunset-amber/70"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            {fetching ? (
              <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-sunset-amber" /></div>
            ) : filteredApps.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-sunset-amber/30 bg-plum-700/40 p-10 text-center text-muted-foreground" data-testid="admin-apps-empty">
                No {filter === "all" ? "" : filter} applications.
              </div>
            ) : (
              <div className="rounded-2xl border border-sunset-amber/20 bg-plum-700/70 overflow-hidden" data-testid="admin-apps-table">
                {filteredApps.map((a) => (
                  <button
                    key={a.application_id}
                    data-testid={`admin-app-row-${a.application_id}`}
                    onClick={() => { setSelected(a); setNote(a.admin_note || ""); }}
                    className="w-full flex items-center gap-4 px-5 py-4 border-b border-sunset-amber/10 last:border-b-0 hover:bg-plum-600/60 transition-colors text-left"
                  >
                    <Avatar className="h-10 w-10 border border-sunset-amber/30">
                      <AvatarImage src={a.user_picture} alt={a.user_name} />
                      <AvatarFallback className="bg-plum-600 text-cream text-xs">{a.user_name?.[0] || "A"}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-cream font-semibold truncate">{a.character_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{a.user_name} · {a.discord_name} · age {a.age}</p>
                    </div>
                    <Badge className={`${STATUS_BADGE[a.status]} border uppercase tracking-wider text-[10px]`}>{a.status}</Badge>
                    <span className="text-xs text-muted-foreground hidden sm:block">{new Date(a.created_at).toLocaleDateString()}</span>
                  </button>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="users">
            <div className="relative mb-6 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="admin-user-search"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-plum-800 border-sunset-amber/30 text-cream placeholder:text-muted-foreground/60 focus-visible:ring-sunset-amber"
              />
            </div>
            <div className="rounded-2xl border border-sunset-amber/20 bg-plum-700/70 overflow-x-auto" data-testid="admin-users-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-sunset-amber/20 text-left">
                    <th className="px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground">User</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground hidden md:table-cell">Email</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground">Role</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground">App Status</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground hidden sm:table-cell">Joined</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="border-b border-sunset-amber/10 last:border-b-0 hover:bg-plum-600/50">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8 border border-sunset-amber/30">
                            <AvatarImage src={u.picture} alt={u.name} />
                            <AvatarFallback className="bg-plum-600 text-cream text-xs">{u.name?.[0] || "U"}</AvatarFallback>
                          </Avatar>
                          <span className="text-cream font-medium">{u.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-muted-foreground hidden md:table-cell">{u.email}</td>
                      <td className="px-5 py-3">
                        <Badge className={`border uppercase tracking-wider text-[10px] ${u.role === "admin" ? "bg-sunset-amber/15 text-sunset-amber border-sunset-amber/50" : "bg-plum-600 text-muted-foreground border-sunset-amber/20"}`}>
                          {u.role}
                        </Badge>
                      </td>
                      <td className="px-5 py-3">
                        {u.application_status ? (
                          <Badge className={`${STATUS_BADGE[u.application_status]} border uppercase tracking-wider text-[10px]`}>{u.application_status}</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-muted-foreground hidden sm:table-cell">{new Date(u.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-3 text-right">
                        {u.user_id !== user.user_id && u.role !== "admin" && (
                          <button
                            data-testid={`admin-delete-user-${u.user_id}`}
                            onClick={() => setDeleteTarget(u)}
                            className="p-2 rounded-lg text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            title={`Remove ${u.name}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground" data-testid="admin-users-empty">No users found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) { setSelected(null); setNote(""); } }}>
        <DialogContent className="bg-plum-800 border-sunset-amber/30 text-cream max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-app-detail">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-bold uppercase tracking-wide text-cream">
                  {selected.character_name}
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  {selected.user_name} · {selected.user_email} · {selected.discord_name}
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4" data-testid="admin-app-meta">
                {[
                  { label: "Age", value: selected.age != null ? `${selected.age} (${selected.dob || "—"})` : "—" },
                  { label: "Region", value: selected.timezone || "—" },
                  { label: "Weekly Hours", value: selected.weekly_hours || "—" },
                  { label: "Experience", value: selected.rp_experience || "—" },
                ].map((f) => (
                  <div key={f.label} className="rounded-xl bg-plum-900/70 border border-sunset-amber/15 p-3">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{f.label}</p>
                    <p className="text-sm text-cream font-medium">{f.value}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-4 mt-2">
                {selected.previous_servers && (
                  <div className="rounded-xl bg-plum-900/70 border border-sunset-amber/15 p-4">
                    <p className="text-xs uppercase tracking-widest text-sunset-amber mb-2">Previous Servers</p>
                    <p className="text-sm text-cream/90" data-testid="admin-app-previous-servers">{selected.previous_servers}</p>
                  </div>
                )}
                {selected.character_goals && (
                  <div className="rounded-xl bg-plum-900/70 border border-sunset-amber/15 p-4">
                    <p className="text-xs uppercase tracking-widest text-sunset-amber mb-2">Character Goals</p>
                    <p className="text-sm text-cream/90" data-testid="admin-app-character-goals">{selected.character_goals}</p>
                  </div>
                )}
                {selected.scenario_fearrp && (
                  <div className="rounded-xl bg-plum-900/70 border border-sunset-amber/15 p-4">
                    <p className="text-xs uppercase tracking-widest text-sunset-amber mb-2">Scenario — Armed Robbery (FearRP)</p>
                    <p className="text-sm text-cream/90 leading-relaxed whitespace-pre-line" data-testid="admin-app-scenario-fearrp">{selected.scenario_fearrp}</p>
                  </div>
                )}
                {selected.scenario_metagaming && (
                  <div className="rounded-xl bg-plum-900/70 border border-sunset-amber/15 p-4">
                    <p className="text-xs uppercase tracking-widest text-sunset-amber mb-2">Scenario — NLR & Metagaming</p>
                    <p className="text-sm text-cream/90 leading-relaxed whitespace-pre-line" data-testid="admin-app-scenario-meta">{selected.scenario_metagaming}</p>
                  </div>
                )}
                <div className="rounded-xl bg-plum-900/70 border border-sunset-amber/15 p-4">
                  <p className="text-xs uppercase tracking-widest text-sunset-amber mb-2">Backstory</p>
                  <p className="text-sm text-cream/90 leading-relaxed whitespace-pre-line" data-testid="admin-app-backstory">{selected.character_backstory}</p>
                </div>
                <div className="rounded-xl bg-plum-900/70 border border-sunset-amber/15 p-4">
                  <p className="text-xs uppercase tracking-widest text-sunset-amber mb-2">Why Southside</p>
                  <p className="text-sm text-cream/90 leading-relaxed whitespace-pre-line" data-testid="admin-app-why-join">{selected.why_join}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Staff Note (sent to applicant)</p>
                  <Textarea
                    data-testid="admin-note-input"
                    rows={3}
                    placeholder="Optional feedback for the applicant..."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="bg-plum-900/70 border-sunset-amber/30 text-cream focus-visible:ring-sunset-amber"
                  />
                </div>
                {selected.status === "pending" ? (
                  <div className="flex gap-3 pt-2">
                    <Button
                      data-testid="admin-approve-btn"
                      onClick={() => decide("accept")}
                      disabled={deciding}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-full"
                    >
                      {deciding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CircleCheck className="h-4 w-4 mr-2" />}
                      Approve & Whitelist
                    </Button>
                    <Button
                      data-testid="admin-reject-btn"
                      onClick={() => decide("reject")}
                      disabled={deciding}
                      className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold rounded-full"
                    >
                      {deciding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CircleX className="h-4 w-4 mr-2" />}
                      Reject
                    </Button>
                  </div>
                ) : (
                  <Badge className={`${STATUS_BADGE[selected.status]} border uppercase tracking-wider text-[10px]`} data-testid="admin-app-final-status">
                    {selected.status}
                  </Badge>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent className="bg-plum-800 border-sunset-amber/30 text-cream" data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-bold uppercase tracking-wide text-cream">
              Remove {deleteTarget?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              This permanently deletes their account, citizen profile, whitelist applications and sessions.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel" className="btn-outline-amber bg-transparent rounded-full border-sunset-amber/40 text-cream hover:bg-sunset-amber/10 hover:text-cream">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-delete-confirm"
              onClick={deleteUser}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-500 text-white font-bold rounded-full"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Footer />
    </div>
  );
}
